'use client';

/**
 * WebQASourceDocCard — Reception raw source-artefact surface.
 *
 * Reads `useDocsStore.response.summary.fast.media_sid`, then calls the
 * `docs.source` BFF strategy via `/ui/data` to fetch the raw markdown.
 * Same gateway pattern as every other capability — no bespoke REST
 * endpoint. The substrate's CQRS-clean side: customer-voice translation
 * lives on the SP, but this card surfaces the actual engineer-authored
 * bytes for transparency / power-user reading.
 *
 * One round-trip per Media SID; React Query caches by media_sid so
 * switching surfaces doesn't refetch.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FileText, Loader2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useDocsStore } from '@/lib/stores/docs-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import type { DocsSourceArtefact } from '@/lib/api/types';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';


export function WebQASourceDocCard() {
  const T = useCardTypography();
  const response = useDocsStore((s) => s.response);
  // `docs_source` is a hard dependency of this card, not a discovered
  // capability — it isn't surfaced by list_available_strategies, so the
  // capability-store lookup is a no-op; fall back to the literal name.
  const sourceStrategy =
    useCapabilityStore((s) => s.strategyName('docs', 'source')) ?? 'docs_source';

  const [open, setOpen] = useState(false);

  const mediaSid = response?.summary?.fast?.media_sid ?? null;

  const { data, isLoading, error } = useQuery<DocsSourceArtefact>({
    queryKey: ['docs', 'source', mediaSid, sourceStrategy],
    queryFn: async () => {
      if (!sourceStrategy || !mediaSid) {
        throw new Error('docs.source strategy or media_sid missing');
      }
      const res = await api.getStrategyData<DocsSourceArtefact>(sourceStrategy, {
        media_sid: mediaSid,
      });
      if (!res.success) throw new Error(res.error || `${sourceStrategy} failed`);
      return res.data;
    },
    enabled: Boolean(mediaSid && sourceStrategy),
    staleTime: 5 * 60_000,  // 5 min — source bytes rarely change mid-session
  });

  // Substrate-presence-driven: nothing to render until there's a response
  // with a media_sid. Pre-V2 profiles (no media_sid) self-suppress too —
  // the absence of a source artefact isn't a state worth painting.
  if (!response || !mediaSid) return null;

  if (isLoading) {
    return (
      <section className={QA_SECTION}>
        <div className="w-full">
          <div className="rounded-2xl border border-border bg-card/50 p-6 flex items-center gap-3 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className={T.body}>Loading source…</span>
          </div>
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className={QA_SECTION}>
        <div className="w-full">
          <div className="rounded-2xl border border-destructive/40 bg-card/50 p-6 flex items-center gap-3 text-destructive">
            <AlertCircle className="w-5 h-5" />
            <span className={T.body}>
              Couldn’t load source. {error instanceof Error ? error.message : ''}
            </span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <div className={QA_BOX}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center justify-between w-full text-left mb-3"
          >
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-primary shrink-0" />
              <div className={cn(T.metadata, 'uppercase tracking-wider text-xs truncate')}>
                {data.source_path} · {data.bytes_len.toLocaleString()} bytes
              </div>
            </div>
            {open ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
          </button>
          {open ? (
            <pre
              className={cn(
                T.body,
                'whitespace-pre-wrap font-mono text-xs leading-relaxed',
                'bg-muted/30 rounded p-4 border border-border/40',
                'max-h-[70vh] overflow-auto',
              )}
            >
              {data.content}
            </pre>
          ) : (
            <p className={cn(T.body, 'text-muted-foreground text-sm font-mono whitespace-pre-line line-clamp-3')}>
              {data.content.slice(0, 300)}…
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
